import React, { useState } from 'react';
import type { SetStateAction } from 'react';
import { ButtonIcon, Modal } from '../components';
import { GLOBAL_ICONS } from '../util/vars';
import type { AllIconType, SizeType, SystemColorType, ThemeColorType } from '../util/types';
import { getIconComponent } from '../util/reactFunc';

type InitialPropsType = {
  size?: SizeType,
  color?: ThemeColorType | SystemColorType,
  title?: string,
  icon?: AllIconType,
  alwaysOnTop?: boolean,
  disableMaskClick?: boolean,
  overflow?: 'hidden' | 'auto',
} & Record<string, any>;

/**
 * To use it like:
 *
 * const [ modal, toggle, setProps ] = useModal((props: any) => (
 *  <div>content</div>
 * ), {}, () => alert('on close!'))
 *
 * @param Content Component content of the modal.
 * @param initialProps Any initial props you want to set to the modal. As size.
 * @param onClose Function to be called when click on mask/x button happens.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export const useModal = (Content: any, initialProps: InitialPropsType = {}, onClose = () => {/* Does nothing */}): [React.ReactElement, (open: boolean) => void, (props: SetStateAction<InitialPropsType>) => void, boolean] => {
  const [ isOpen, setOpen ] = useState(false);
  const [ props, setProps ] = useState<InitialPropsType>(initialProps);

  const toggle = (isOpen?: boolean) => setOpen((prev) => null != isOpen ? isOpen : !prev);

  const component = (
    <Modal
      open={isOpen}
      onClose={onClose}
      toggle={toggle}
      size={props?.size}
      color={props?.color}
      alwaysOnTop={props?.alwaysOnTop}
      disableMaskClick={props?.disableMaskClick}
      overflow={props?.overflow}
    >
      <div>
        {!props?.disableHeader && (
          <div className={`container flex items-center pb-md border-b-2 border-${props?.color ?? 'header'}`}>
            <div className='flex items-center'>
              {props?.icon &&
                <span className={'mr-sm'}>
                  {getIconComponent(props?.icon, 'md', 'dark')}
                </span>
              }
              {props?.title && (
                <h5 className={'font-bold'}>{props?.title}</h5>
              )}
            </div>
            <div className={'ml-auto'}>
              <ButtonIcon
                icon={GLOBAL_ICONS.close}
                onClick={() => {
                  toggle(false);
                  onClose();
                }}
                size={'sm'}
                color={'light'}
                isOutline
                isCircle
              />
            </div>
          </div>
        )}
        <Content toggle={toggle} {...props} />
      </div>
    </Modal>
  );

  return [ component, toggle, setProps, isOpen ];
};
